import React, { useEffect, useState } from 'react';
import { WEBROUTES } from '@zonofi/zono-money-helpers';
import {
  BrowserRouter,
  Routes,
  Route,
  useSearchParams,
} from 'react-router-dom';
import {
  InviteLoadingScreen,
  OrderSuccessfull,
  Opps,
  CatalogScreen,
  CatalogImageSearchScreen,
  OrderDetailsScreenV2,
  UnauthorizedScreen,
  ProductDetailsScreen,
  ReorderScreen,
  ErrorScreenV2,
  GstScreen,
  EmbeddedSignupScreen,
  SurveyFormScreen,
  GalleryViewScreen,
  PaymentAdvice,
  InvoicePreviewScreen,
  InvoiceScreen,
  PaymentScreen,
  PaymentDetailsScreen,
  SideDrawer,
  LedgerScreen,
  DialerRedirectScreen,
} from '../../screens';
import {
  APP_TYPE,
  CustomerInviteDetailsResponse,
  CustomerInviteResponse,
  OnGuestUserAuthenticated,
  dispatch,
  getCustomerInvite,
  getIsSideDrawerOpen,
  getIsTourActive,
  getToken,
  getWorkspaceDefaults,
  onSuccess,
  updateAppType,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { TourController } from '@zonofi/zono-money-tour';
import { i18nService, SupportedCurrency } from '@zonofi/i18n';
import { LoadingSpinner } from '@zonofi/zono-money-design-system';
import axios from 'axios';
import { getConfigValue } from '@zonofi/common';

export const AppNavigator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const inviteId = searchParams.get('inviteId');
  const sellerWorkspaceId = searchParams.get('sellerWorkspaceId');
  const isTourActive = useAppSelector(getIsTourActive);
  const workspaceDefaults = useAppSelector(getWorkspaceDefaults);
  const storeToken = useAppSelector(getToken);
  const inviteData = useAppSelector(getCustomerInvite);
  const isSideDrawerOpen = useAppSelector(getIsSideDrawerOpen);
  const [isLoading, setIsLoading] = useState(true);

  const requiresGetInviteDetails =
    !location.pathname.startsWith('/invite') &&
    token !== storeToken &&
    inviteData?.seller?.name === '';

  console.log('requiresGetInviteDetails: ', requiresGetInviteDetails);

  useEffect(() => {
    if (requiresGetInviteDetails) {
      const baseUrl = getConfigValue('API_ENDPOINT');
      console.log('baseUrl: ', baseUrl);
      const url = `${baseUrl}hub/orgs/api/customer/details`;
      console.log('url: ', url);
      axios
        .get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            inviteId: inviteId,
            sellerWorkspaceId: sellerWorkspaceId,
            customerId: inviteId,
            includeInactive: false,
            includeCFA: false,
          },
        })
        .then((response) => {
          const responseData = response?.data as CustomerInviteDetailsResponse;
          const storeData: CustomerInviteResponse = {
            inviteId: responseData?.inviteId,
            seller: {
              name: responseData?.workspace?.spaceName,
              phone: responseData?.workspace?.mobile || '',
              email: responseData?.workspace?.companyEmail,
              workspaceId: sellerWorkspaceId,
              profileImg: responseData?.sellerWorkspaceImage,
            },
            buyer: {
              name: responseData?.companyName,
              phone: responseData?.telephone,
              workspaceId: '',
              inviteType: responseData?.invite?.inviteType,
            },
          };
          if (responseData?.waConfig) {
            storeData.waConfig = {
              ...(responseData?.waConfig || {}),
              wabaId: responseData?.waConfig?.wabaId,
              whatsappPhoneNo: responseData?.waConfig?.whatsappPhoneNo,
            };
          }
          if (responseData.workspace) {
            storeData.workspaceConfig = {
              currency: responseData.workspace
                .defaultCurrencyCode as SupportedCurrency,
              timezone: responseData.workspace.timezone,
            };
          }
          dispatch(onSuccess(storeData));
          dispatch(OnGuestUserAuthenticated(token));
          setIsLoading(false);
        })
        .catch((err) => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [token, storeToken, inviteData, inviteId, sellerWorkspaceId]);

  useEffect(() => {
    if (workspaceDefaults) {
      i18nService.updateConfig({
        currency: workspaceDefaults.currency as SupportedCurrency,
        timezone: workspaceDefaults.timezone,
      });
    }
  }, [workspaceDefaults]);

  useEffect(() => {
    dispatch(updateAppType(APP_TYPE.ZONO_MONEY));
  }, []);

  useEffect(() => {
    function getLocalWithExpiry<T>(key: string, fallback: boolean): boolean {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;

      try {
        const expiresAt = JSON.parse(raw);

        if (Date.now() > expiresAt) {
          localStorage.removeItem(key);
          return fallback;
        }

        return true;
      } catch {
        localStorage.removeItem(key);
        return fallback;
      }
    }
    const isTeamMember = getLocalWithExpiry('isTeamMember', false);
    if (isTeamMember) {
      const parsedUrl = new URL(window.location.href);
      const poFileId = parsedUrl.searchParams.get('poFileId');
      if (poFileId) {
        window.location.href = `/seller/order/details/${poFileId}?source=external`;
      } else {
        window.location.href = '/seller/login?source=external';
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div className="invite-loading-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="*" element={<UnauthorizedScreen />} />
        <Route path={WEBROUTES.ES_SCREEN} element={<EmbeddedSignupScreen />} />
        <Route
          path={WEBROUTES.CUSTOMER_INVITE_LOADING_SCREEN}
          element={<InviteLoadingScreen />}
        >
          <Route path=":inviteId" element={<InviteLoadingScreen />} />
        </Route>
        <Route path={WEBROUTES.GST_SCREEN} element={<GstScreen />} />
        <Route path={WEBROUTES.KYC_SCREEN} element={<GstScreen />} />

        <Route path={WEBROUTES.OPPS} element={<Opps />} />
        {/* <Route path={WEBROUTES.FACEBOOK} element={<FacebookScreen />}>
          <Route path=":id" element={<FacebookScreen />} />
        </Route> */}

        <Route
          path={WEBROUTES.ORDER_SUCCESSFULL}
          element={<OrderSuccessfull />}
        />
        <Route
          path={WEBROUTES.CATALOGUE_IMAGE_SEARCH}
          element={<CatalogImageSearchScreen />}
        />
        <Route path={WEBROUTES.LEDGER} element={<LedgerScreen />} />
        <Route
          path={WEBROUTES.PRODUCT_DETAILS}
          element={<ProductDetailsScreen />}
        >
          <Route path=":productId" element={<ProductDetailsScreen />} />
        </Route>
        <Route path={WEBROUTES.ORDER} element={<OrderDetailsScreenV2 />}>
          <Route path=":orderId" element={<OrderDetailsScreenV2 />} />
        </Route>
        <Route path={WEBROUTES.PAYMENTS} element={<PaymentScreen />}>
          <Route path=":paymentId" element={<PaymentScreen />} />
        </Route>
        <Route path={WEBROUTES.PAYMENT} element={<PaymentDetailsScreen />}>
          <Route path=":paymentId" element={<PaymentDetailsScreen />} />
        </Route>
        <Route path={WEBROUTES.INVOICE_SCREEN} element={<InvoiceScreen />}>
          <Route path=":invoiceId" element={<InvoiceScreen />} />
        </Route>
        <Route path={WEBROUTES.INVOICE} element={<InvoicePreviewScreen />}>
          <Route path=":invoiceId" element={<InvoicePreviewScreen />} />
        </Route>
        <Route path={WEBROUTES.CART} element={<OrderDetailsScreenV2 />} />

        <Route path={WEBROUTES.CAMPAIGN} element={<CatalogScreen />}>
          <Route path=":campaignId" element={<CatalogScreen />} />
        </Route>
        <Route path={WEBROUTES.PRICELIST} element={<CatalogScreen />}>
          <Route path=":pricelistId" element={<CatalogScreen />} />
        </Route>
        <Route path={WEBROUTES.CATALOGUE} element={<CatalogScreen />} />
        <Route path={WEBROUTES.REORDER} element={<ReorderScreen />}>
          <Route path=":orderId" element={<ReorderScreen />} />
        </Route>
        <Route path={WEBROUTES.SURVEY_FORM} element={<SurveyFormScreen />}>
          <Route path=":formId" element={<SurveyFormScreen />} />
        </Route>
        <Route path={WEBROUTES.PAYMENT_ADVICE} element={<PaymentAdvice />}>
          <Route path=":orderId" element={<PaymentAdvice />} />
        </Route>
        <Route path={WEBROUTES.CATALOG_TOOL} element={<GalleryViewScreen />}>
          <Route path=":folderId" element={<GalleryViewScreen />} />
        </Route>
        <Route
          path={WEBROUTES.DIALER_REDIRECT}
          element={<DialerRedirectScreen />}
        />

        <Route
          path={WEBROUTES.UNAUTHORIZED_SCREEN}
          element={<UnauthorizedScreen />}
        />
        <Route path={WEBROUTES.ERROR_SCREEN} element={<ErrorScreenV2 />} />
      </Routes>

      {isTourActive && <TourController />}
      {isSideDrawerOpen && <SideDrawer />}
    </>
  );
};
